import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import { FlatList, StyleSheet, TouchableOpacity, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import { useRouter } from 'expo-router'

import { CardString } from 'pokersolver'
import { THREE_CARD_BOT_NAMES } from '@ppwr/poker'
import { useStore } from '../../models'
import { Card } from '../../components/ANewDesign/Card'
import { palette } from '../../theme/palette'
import { ScrollView } from 'react-native-gesture-handler'
import SuitPattern from './assets/SuitPattern.png'
import Spade from '../../components/ANewDesign/Card/assets/color2/spade.svg'
import { fontFamily } from '../../theme'
import { Image } from 'react-native'
import { ThreeCardTop } from './ThreeCardTop'
import { useCountdown } from '../../hooks/useCountdown'
import { DS } from '../../design-system'
import { ensurePushEnabled, isPushGranted } from '@/modules/push'
import { getLocalTimeForTomorrowAtSix } from '../../utils'

export const ThreeCardGameOverScreen = observer(() => {
  const router = useRouter()
  const {
    game: { getThreeCardResult, threeCardResult },
    permissions: { setIsPushDisabled },
    session: { user, updateUserPushNotificationConfig }
  } = useStore()

  const [selectedRound, setSelectedRound] = useState(1)
  const tomorrowAtSix = getLocalTimeForTomorrowAtSix()
  const [, hours, minutes, seconds] = useCountdown(tomorrowAtSix)

  const fetchResults = useCallback(async () => {
    await getThreeCardResult()
  }, [getThreeCardResult])

  useEffect(() => {
    fetchResults()
  }, [fetchResults])

  const myRounds = threeCardResult.map((round) => round.players?.find((p) => p.name === user?.name))
  const amountOfRounds = myRounds.filter((r) => Boolean(r?.chips)).length
  const amountOfWonRounds = threeCardResult.filter((round) => round.winners[0].playerName === user?.name).length
  const handInfo = threeCardResult.find((round) => round.hand === selectedRound)
  const winnerInfo = handInfo?.winners[0]

  const isGamePlayEnabled = user?.pushNotificationConfig?.gamePlay ?? false
  const isDailyPokerishEnabled = user?.pushNotificationConfig?.dailyPokerish ?? false

  const getHandPlayersInfo = () =>
    [user?.name, ...THREE_CARD_BOT_NAMES]
      .map((name) => {
        const playerInfo = handInfo?.players?.find((p) => p.name === name)
        if (!playerInfo) return null
        return {
          name,
          cards: (playerInfo.cards ?? []) as CardString[],
          won: winnerInfo.playerName === name
        }
      })
      .filter(Boolean)

  const communityCard = () => {
    if (!handInfo) return

    const winningCards = winnerInfo.winningCards as CardString[]
    const holeCards = winnerInfo.cards
    if (!holeCards) return

    return winningCards.filter((card) => !holeCards.includes(card))
  }

  const handleNext = () => {
    router.navigate('/game/three-card/share')
  }

  const onReminderPress = async () => {
    if (isDailyPokerishEnabled) {
      return
    }

    const hasPermission = await isPushGranted()
    setIsPushDisabled(!hasPermission)

    if (!hasPermission) {
      ensurePushEnabled(true)
      return
    }

    const pushConfig = {
      dailyPokerish: isDailyPokerishEnabled,
      gamePlay: isGamePlayEnabled
    }

    updateUserPushNotificationConfig({ ...pushConfig, dailyPokerish: true })
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: palette.cream, flexGrow: 1 }}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ gap: 24, backgroundColor: palette.cream, paddingBottom: 24 }}
      >
        <ThreeCardTop end endText="Game Summary" />
        <StatusBar animated={true} style={'dark' as StatusBarStyle} hidden={false} />

        <DS.Frame gap="24" style={{ paddingHorizontal: 16 }}>
          <View style={styles.titleContainer}>
            {amountOfWonRounds ? (
              <DS.Text variant="Heading 2/Medium">
                You won {amountOfWonRounds} round{amountOfWonRounds > 1 ? 's' : ''}!
              </DS.Text>
            ) : (
              <>
                <DS.Text variant="Heading 2/Medium">No wins for you today.</DS.Text>
                <DS.Text variant="Heading 2/Medium">Try tomorrow!</DS.Text>
              </>
            )}
          </View>
          <View style={{ overflow: 'hidden' }}>
            <Image style={{ width: '100%' }} source={SuitPattern} resizeMode="stretch" />
            <View style={styles.content}>
              <Rounds
                amountOfRounds={amountOfRounds}
                selectedRound={selectedRound}
                onSelect={(selected) => setSelectedRound(selected)}
              />
              {/* </View> */}
              <View style={styles.playersContainer}>
                <PlayerSummary name="Community Card" cards={communityCard()} communityCard />
                {getHandPlayersInfo().map(
                  (p) =>
                    p && (
                      <PlayerSummary
                        name={p.name === user?.name ? 'You' : (p.name ?? '')}
                        bold={p.name === user?.name}
                        cards={p.cards}
                        won={p.won}
                      />
                    )
                )}
              </View>
              <DS.Frame row alignItems="center" flex={1} gap="6" justifyContent="center">
                {[1, 2, 3, 4, 5].map((round) => (
                  <View
                    key={round}
                    style={{
                      width: 6,
                      height: 6,
                      backgroundColor:
                        selectedRound === round ? DS.palette['Brand/Maroon'] : DS.palette['Brand/Light Grey'],
                      borderRadius: 9999
                    }}
                  />
                ))}
              </DS.Frame>
              {/* </View> */}
            </View>
          </View>
          <View style={styles.nextGameContainer}>
            <Spade />
            <DS.Text variant="Heading 4/Medium">Next game available</DS.Text>
            <DS.Text variant="Body Medium/Regular">
              {hours} hrs {minutes} min {seconds} sec
            </DS.Text>
            <DS.Button
              style={{
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onPress={onReminderPress}
              variant="Medium/Secondary"
              type="On Dark"
              centerContent
              leftIcon={isDailyPokerishEnabled ? <DS.Icon name="Check" color="Global/Black" size={16} /> : null}
            >
              {isDailyPokerishEnabled ? 'Reminder is set' : 'Set Reminder'}
            </DS.Button>
          </View>
        </DS.Frame>

        <DS.Frame style={{ paddingHorizontal: 16 }}>
          <DS.Button variant="Large/Primary" type="On Light" onPress={handleNext}>
            Share your win card
          </DS.Button>
        </DS.Frame>
      </ScrollView>
    </SafeAreaView>
  )
})

const styles = StyleSheet.create({
  container: {
    // flex: 1,
    backgroundColor: palette.cream
    // justifyContent: 'space-between',
    // alignItems: 'center',    // paddingVertical: 40
  },

  content: {
    // flex: 0.8,
    overflow: 'hidden',
    gap: 16,
    backgroundColor: palette.white,
    // justifyContent: 'center',
    // alignItems: 'center',
    borderWidth: 1,
    borderColor: palette.lightGrey,
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    paddingVertical: 24,
    paddingHorizontal: 16
  },
  titleContainer: {
    alignItems: 'center'
  },
  summary: {
    // flex: 1,
    justifyContent: 'space-between',
    gap: 10
  },
  roundsContainer: {
    // flex: 1,
    flexDirection: 'row',
    alignItems: 'center'
    // gap: 20
  },
  roundText: {
    fontFamily: fontFamily.FeatureDeckRegular,
    fontSize: 22,
    color: palette.lightGrey,
    textAlign: 'center'
  },
  playersContainer: {
    // flex: 4,
    flexDirection: 'row',
    gap: 16,
    justifyContent: 'center',
    alignItems: 'flex-start',
    alignSelf: 'stretch'
    // alignSelf: stretch;
  },
  nextGameContainer: {
    // flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8
  },
  nextGameTitle: {
    fontFamily: fontFamily.FeatureDeckMedium,
    fontSize: 22
  },
  nextGameSubtitle: {
    fontFamily: fontFamily.GTAmericaStandardRegular,
    fontSize: 16
  }
})

const Rounds = ({
  amountOfRounds,
  selectedRound,
  onSelect
}: {
  amountOfRounds: number
  selectedRound: number
  onSelect: (selected: number) => void
}) => {
  return (
    <FlatList
      contentContainerStyle={[styles.roundsContainer, { gap: 16 }]}
      style={{ borderColor: palette.lightGrey, borderTopWidth: 1, borderBottomWidth: 1, paddingVertical: 16 }}
      data={Array.from({ length: amountOfRounds })}
      renderItem={({ index }) => (
        <TouchableOpacity onPress={() => onSelect(index + 1)}>
          <DS.Text
            variant="Heading 4/Medium"
            style={{ color: index + 1 === selectedRound ? palette.black : palette.lightGrey }}
          >
            Round {index + 1}
          </DS.Text>
        </TouchableOpacity>
      )}
      horizontal
    />
  )
}

const PlayerSummary = ({
  name,
  won,
  cards,
  communityCard,
  bold
}: {
  name: string
  won?: boolean
  cards?: CardString[]
  communityCard?: boolean
  bold?: boolean
}) => {
  return (
    <View style={[playerSummaryStyles.container, communityCard ? { maxWidth: 62 } : {}]}>
      <View style={playerSummaryStyles.cards}>
        <Card card={cards?.[0]} fontSize={16} shadow />
        {!communityCard ? <Card card={cards?.[1]} fontSize={16} shadow /> : null}
      </View>
      <DS.Text
        variant={communityCard ? 'Mini/Regular' : 'Body Large/Medium'}
        color={bold || communityCard ? 'Global/Black' : 'Brand/Dark Grey'}
        textAlign="center"
      >
        {name}
      </DS.Text>
      <View style={{}}>
        {won ? (
          <View style={playerSummaryStyles.wonContainer}>
            <DS.Text color="Global/White" variant="Caption/Bold">
              WON
            </DS.Text>
          </View>
        ) : null}
      </View>
    </View>
  )
}

const playerSummaryStyles = StyleSheet.create({
  container: {
    // flex: 1,
    gap: 8,
    justifyContent: 'flex-start',
    alignItems: 'center'
  },
  cards: {
    // flex: 1,
    flexDirection: 'row',
    gap: 1,
    alignItems: 'center'
  },
  name: {
    fontFamily: fontFamily.GTAmericaStandardRegular,
    fontSize: 18,
    color: palette.grey,
    textAlign: 'center'
  },
  wonContainer: {
    backgroundColor: DS.palette['Brand/Orange'],
    padding: 8,
    borderRadius: 16,
    paddingVertical: 6,
    paddingHorizontal: 10
  },
  wonText: {
    fontSize: 12,
    color: palette.white,
    fontFamily: fontFamily.GTAmericaExtendedMedium
  }
})
